import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import { recruitingAPI } from "../../services/recruitingApi";

const SystemSettingsTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [agencyMode, setAgencyMode] = useState<boolean>(false);
  const [isUpdatingAgencyMode, setIsUpdatingAgencyMode] =
    useState<boolean>(false);
  const [apolloSearchCountry, setApolloSearchCountry] = useState<string>("");
  const [isUpdatingApolloCountry, setIsUpdatingApolloCountry] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchAgencyMode(), fetchApolloSearchCountry()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchAgencyMode = async (): Promise<void> => {
    try {
      const response = (await recruitingAPI.getSystemConfig("agency_mode")) as {
        data?: { value?: string };
      };
      setAgencyMode(response.data?.value === "true");
    } catch (error) {
      console.error("Error fetching agency mode:", error);
    }
  };

  const fetchApolloSearchCountry = async (): Promise<void> => {
    try {
      const response = (await recruitingAPI.getSystemConfig(
        "apollo_search_country",
      )) as { data?: { value?: string } };
      setApolloSearchCountry(response.data?.value || "");
    } catch (error) {
      console.error("Error fetching Apollo search country:", error);
    }
  };

  const handleToggleAgencyMode = async (): Promise<void> => {
    setIsUpdatingAgencyMode(true);
    try {
      const newValue = agencyMode ? "false" : "true";
      await recruitingAPI.setSystemConfig("agency_mode", newValue);
      const newAgencyMode = !agencyMode;
      setAgencyMode(newAgencyMode);
      alert(
        t("settings.agencyMode.toggleSuccess", {
          status: newAgencyMode
            ? t("settings.agencyMode.enabled")
            : t("settings.agencyMode.disabled"),
        }),
      );
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      console.error("Error updating agency mode:", error);
      alert(
        "Error updating agency mode: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    } finally {
      setIsUpdatingAgencyMode(false);
    }
  };

  const handleUpdateApolloSearchCountry = async (): Promise<void> => {
    setIsUpdatingApolloCountry(true);
    try {
      await recruitingAPI.setSystemConfig(
        "apollo_search_country",
        apolloSearchCountry,
      );
      alert(t("settings.apolloCountry.saveSuccess"));
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      console.error("Error updating Apollo search country:", error);
      alert(
        "Error updating Apollo search country: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    } finally {
      setIsUpdatingApolloCountry(false);
    }
  };

  if (loading) {
    return <div className="loading">{t("settings.loading")}</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <Settings size={16} /> {t("settings.title")}
        </h2>
      </div>

      <div className="config-section">
        <div className="config-item">
          <div className="config-info">
            <h3>{t("settings.agencyMode.title")}</h3>
            <p>{t("settings.agencyMode.description")}</p>
          </div>
          <div className="config-control">
            <label className="toggle-switch">
              <span className="sr-only">{t("settings.agencyMode.toggle")}</span>
              <input
                type="checkbox"
                checked={agencyMode}
                onChange={handleToggleAgencyMode}
                disabled={isUpdatingAgencyMode}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="config-status">
              {agencyMode
                ? t("settings.agencyMode.enabled")
                : t("settings.agencyMode.disabled")}
            </span>
          </div>
        </div>

        <div className="config-item">
          <div className="config-info">
            <h3>{t("settings.apolloCountry.title")}</h3>
            <p>{t("settings.apolloCountry.description")}</p>
          </div>
          <div className="config-control apollo-country-control">
            <input
              type="text"
              value={apolloSearchCountry}
              onChange={(e) => setApolloSearchCountry(e.target.value)}
              placeholder={t("settings.apolloCountry.placeholder")}
              disabled={isUpdatingApolloCountry}
              className="country-input"
            />
            <button
              className="btn-primary btn-sm"
              onClick={handleUpdateApolloSearchCountry}
              disabled={isUpdatingApolloCountry}
            >
              {isUpdatingApolloCountry
                ? t("common:actions.saving")
                : t("common:actions.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsTab;
